import { useState, useEffect } from 'react';
import { notificacoesMock, type Notificacao, type TipoNotificacao } from '../../../mocks/notificacoes';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<TipoNotificacao>('todos');
  const [filtroStatus, setFiltroStatus] = useState<'todas' | 'lidas' | 'nao_lidas'>('todas');

  useEffect(() => {
    // Carregar notificações do localStorage ou usar mock
    const stored = localStorage.getItem('notificacoes');
    if (stored) {
      setNotificacoes(JSON.parse(stored));
    } else {
      setNotificacoes(notificacoesMock);
      localStorage.setItem('notificacoes', JSON.stringify(notificacoesMock));
    }
  }, []);

  const salvarNotificacoes = (novasNotificacoes: Notificacao[]) => {
    setNotificacoes(novasNotificacoes);
    localStorage.setItem('notificacoes', JSON.stringify(novasNotificacoes));
  };

  const marcarComoLida = (id: string) => {
    const atualizadas = notificacoes.map(n => 
      n.id === id ? { ...n, lida: true } : n
    );
    salvarNotificacoes(atualizadas);
  };

  const marcarTodasComoLidas = () => {
    const atualizadas = notificacoes.map(n => ({ ...n, lida: true }));
    salvarNotificacoes(atualizadas);
  };

  const excluirNotificacao = (id: string) => {
    const atualizadas = notificacoes.filter(n => n.id !== id);
    salvarNotificacoes(atualizadas);
  };

  const notificacoesFiltradas = notificacoes.filter(n => {
    const passaTipo = filtroTipo === 'todos' || n.tipo === filtroTipo;
    const passaStatus = 
      filtroStatus === 'todas' || 
      (filtroStatus === 'lidas' && n.lida) || 
      (filtroStatus === 'nao_lidas' && !n.lida);
    return passaTipo && passaStatus;
  });

  const formatarDataHora = (dataHora: string) => {
    const data = new Date(dataHora);
    const agora = new Date();
    const diffMs = agora.getTime() - data.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMs / 3600000);
    const diffDias = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHoras < 24) return `${diffHoras}h atrás`;
    if (diffDias === 1) return 'Ontem';
    if (diffDias < 7) return `${diffDias} dias atrás`;
    
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Painel de Notificações */}
      <div className="fixed top-20 right-4 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 overflow-hidden animate-[slideDown_0.3s_ease-out]">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-amber-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Notificações</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/50 transition-colors"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
          </div>

          {/* Ações Rápidas */}
          <div className="flex items-center gap-2">
            <button
              onClick={marcarTodasComoLidas}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
            >
              Marcar todas como lidas
            </button>
            <span className="text-gray-300">•</span>
            <a
              href="/dashboard/notificacoes"
              className="text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
            >
              Configurações
            </a>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-600">Tipo:</span>
            <div className="flex flex-wrap gap-1">
              {[
                { value: 'todos', label: 'Todas' },
                { value: 'carta_entregue', label: 'Cartas' },
                { value: 'lembrete_entrega', label: 'Lembretes' },
                { value: 'pagamento_confirmado', label: 'Pagamentos' }
              ].map(tipo => (
                <button
                  key={tipo.value}
                  onClick={() => setFiltroTipo(tipo.value as TipoNotificacao)}
                  className={`px-2 py-1 text-xs rounded-full transition-all ${
                    filtroTipo === tipo.value
                      ? 'bg-amber-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tipo.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Status:</span>
            <div className="flex gap-1">
              {[
                { value: 'todas', label: 'Todas' },
                { value: 'nao_lidas', label: 'Não lidas' },
                { value: 'lidas', label: 'Lidas' }
              ].map(status => (
                <button
                  key={status.value}
                  onClick={() => setFiltroStatus(status.value as any)}
                  className={`px-2 py-1 text-xs rounded-full transition-all ${
                    filtroStatus === status.value
                      ? 'bg-amber-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lista de Notificações */}
        <div className="max-h-[500px] overflow-y-auto">
          {notificacoesFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-amber-50 mb-4">
                <i className="ri-notification-off-line text-4xl text-amber-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma notificação</h4>
              <p className="text-sm text-gray-600 text-center">
                Você está em dia! Não há notificações para exibir no momento.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notificacoesFiltradas.map((notificacao, index) => (
                <div
                  key={notificacao.id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors relative group animate-[fadeIn_0.3s_ease-out] ${
                    !notificacao.lida ? 'bg-amber-50/30' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Indicador de não lida */}
                  {!notificacao.lida && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-amber-600 rounded-full"></div>
                  )}

                  <div className="flex gap-3">
                    {/* Ícone */}
                    <div 
                      className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ backgroundColor: `${notificacao.cor}15` }}
                    >
                      <i 
                        className={`${notificacao.icone} text-xl`}
                        style={{ color: notificacao.cor }}
                      ></i>
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className={`text-sm font-semibold ${
                          notificacao.lida ? 'text-gray-700' : 'text-gray-900'
                        }`}>
                          {notificacao.titulo}
                        </h4>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatarDataHora(notificacao.dataHora)}
                        </span>
                      </div>
                      <p className={`text-sm ${
                        notificacao.lida ? 'text-gray-500' : 'text-gray-700'
                      }`}>
                        {notificacao.descricao}
                      </p>

                      {/* Ações */}
                      <div className="flex items-center gap-3 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!notificacao.lida && (
                          <button
                            onClick={() => marcarComoLida(notificacao.id)}
                            className="text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
                          >
                            Marcar como lida
                          </button>
                        )}
                        <button
                          onClick={() => excluirNotificacao(notificacao.id)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium transition-colors"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notificacoesFiltradas.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-600">
              Mostrando {notificacoesFiltradas.length} de {notificacoes.length} notificações
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}